import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { untilDestroyed } from '@ngneat/until-destroy';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { instanceToPlain } from 'class-transformer';

import { EventEntity, GroupEntity, GroupEventEntity, GroupParticipant, PaginatedEntity, Pagination, TaskEntity, UserEntity } from '@core/entities';
import { UseGroups } from '@core/usecases/useCommunityGroups';
import type { FilterQuery } from '@core/types';
import { ConfirmationDialogComponent, EventDetailDialogComponent, SingleInputCreateComponent } from '@web/_shared/dialogs';
import { ApprovedStatus, ConfirmationDialogType, DeleteConfirmationOptions, GroupPrivate, NotificationType, SocketEvents, UpdateTabType } from '@core/enums';
import { FormBuilder, Validators } from '@angular/forms';
import { FormService, Logger } from '@core/services';
import { EnumToTabsSelectionArray, getAlertTypeByApprovedStatusOfEntity, GetKeycloakId, GetTeamId, GetUserId, isMobileView, scrollToTop, stripHtml, updateObjectProperties } from '@core/utils';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { UseClub } from '@core/usecases';
import { PermissionService } from '@auth';
import { SocketIoService } from '@core/socket-io';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ButtonComponent } from '@shared/components/ui';

const log = new Logger('Group Composition');

export class GroupComposition extends FormService {
  // Group List Component
  // isLoading = true;
  selectedImage: File | string;
  groups: GroupEntity[] = [];
  pagination: Pagination = new PaginatedEntity().pagination;
  filterQuery: FilterQuery = {};
  selectedFilter = 1;

  // Group Details Component
  loadings = {
    groupDetails: true,
    eventList: true,
    memberList: true,
    newsList: true,
    taskList: true,
  };

  groupEvents: GroupEventEntity[] = [];
  groupMembers: GroupParticipant[] = [];
  filteredMembers: GroupParticipant[] = [];
  groupTasks: TaskEntity[] = [];

  // For Group form
  groupId = 0;
  teamUsers: UserEntity[] = [];
  allTeamUsers: UserEntity[] = [];
  currentUser: UserEntity;
  groupDetails = new GroupEntity();

  // These below 4 properties are used to show admin the changes requested by the user
  originalRecord: GroupEntity;
  updatedRecord: GroupEntity;
  updateSelectionTabs: { label: string; displayName: string }[] = [];
  currentUpdatedRecordTab = UpdateTabType.CURRENT;

  protected readonly _toast = inject(HotToastService);
  protected readonly _translate = inject(TranslateService);
  protected readonly _dialogService = inject(DialogService);
  protected _dialogRef: DialogRef<any>;
  protected readonly _router = inject(Router);
  protected bottomSheetService = inject(NgxBottomSheetService);
  protected readonly _useGroups = new UseGroups();
  protected readonly _permissionService = inject(PermissionService);

  private readonly _useClub = new UseClub();
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _socketService = inject(SocketIoService);

  constructor() {
    super();

    this._socketService
      .fromEvent(SocketEvents.GetNotification)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data: NotificationType) => {
          if (data === NotificationType.Group) {
            if (this.groupId) {
              this._fetchGroupDetails();
            } else {
              this.fetchAllGroups();
            }
          }
        },
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // -------------------------------------------- Getters & Get Methods --------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  get isUserAdmin(): boolean {
    return this._permissionService.isAdmin();
  }

  get isUserAuthor(): boolean {
    return +this.groupDetails?.createdBy === +GetUserId();
  }

  get isUserGroupAdmin() {
    return +this.groupDetails?.groupCreator?.id === +GetUserId();
  }

  get alertType() {
    if (!this.groupDetails) return false;
    return getAlertTypeByApprovedStatusOfEntity(this.groupDetails);
  }

  get canUserPublishAndDenyGroup(): boolean {
    return this.isUserAdmin && this.groupDetails.approvedStatus === ApprovedStatus.UnApproved && !this.groupDetails?.denyById;
  }

  get canUserJoinGroup(): boolean {
    return !this.groupDetails.isAdmin && !this.groupDetails.isMember && this.groupDetails.approvedStatus === ApprovedStatus.Approved;
  }

  get canUserLeaveGroup(): boolean {
    return this.groupDetails.isApprovedMember && !this.groupDetails.isAdmin && this.groupDetails.approvedStatus === ApprovedStatus.Approved;
  }

  get canUserEditOrDeleteGroup(): boolean {
    return this.groupDetails.isAdmin || this.isUserAdmin;
  }

  get canUserSeeGroupTasks(): boolean {
    return this.isUserAdmin || this.groupDetails.isAdmin || this.groupDetails.isApprovedMember || this.groupDetails.isMember;
  }

  /**
   * Determines if participant layout (tasks + chat) should be shown while loading.
   * Show participant skeletons during initial load for better UX; once loaded,
   * fall back to actual permission via canUserSeeGroupTasks.
   */
  get canUserSeeGroupTasksEvenIfLoading(): boolean {
    return this.loadings.groupDetails || this.canUserSeeGroupTasks;
  }

  get isGroupAwaitingUpdateApproval(): boolean {
    return !!this.groupDetails.updatedRecord || this.groupDetails?.approvedStatus === ApprovedStatus.UnApproved;
  }

  get showUpdatedRecord(): boolean {
    return this.originalRecord?.updatedRecord && !(this.originalRecord?.denyById && this.originalRecord?.denyByUser);
  }

  get groupParticipants(): GroupParticipant[] {
    return this.currentUpdatedRecordTab === UpdateTabType.CURRENT ? this.groupMembers : this.updatedRecord?.participants;
  }

  getMemberGroupParticipantStatus(group: GroupEntity): ApprovedStatus {
    return group.participants?.find((participant) => participant.userId === GetUserId())?.approvedStatus;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------ Action Methods -----------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  paginationChange(pagination: Pagination) {
    this.pagination = pagination;
    this.isLoading = true;
    this.fetchAllGroups();
    const scrollQuery = isMobileView() ? null : '.card.groups-list';
    scrollToTop(scrollQuery);
  }

  onFilterChange(filter: any) {
    this.isLoading = true;
    if (filter?.id === this.selectedFilter) {
      return;
    }
    this.selectedFilter = filter.id;
    this.isLoading = true;
    switch (filter.value) {
      case 'createdBy':
        this.filterQuery = { createdBy: true };
        break;
      case 'joinByCurrentUser':
        this.filterQuery = { joinByCurrentUser: true };
        break;
      default:
        this.filterQuery = null;
        break;
    }
    this.fetchAllGroups();
  }

  onUpdateRecordTabSelection(tabName: UpdateTabType | string) {
    switch (tabName) {
      case UpdateTabType.CURRENT:
        this.groupDetails = this.originalRecord;
        this.currentUpdatedRecordTab = UpdateTabType.CURRENT;
        break;
      case UpdateTabType.UPDATE:
        this.groupDetails = this.updatedRecord;
        this.currentUpdatedRecordTab = UpdateTabType.UPDATE;
        break;
      default:
        break;
    }
  }

  viewGroupDetails(group: GroupEntity) {
    const canViewDetails = !group.isPrivate || group?.isAdmin || this.getMemberGroupParticipantStatus(group) === ApprovedStatus.Approved || this._permissionService.isAdmin();

    if (canViewDetails) {
      this._router.navigate([`groups/${group.id}/details`]);
    } else {
      let message = 'This is a private group. You need to request to join this group';

      switch (this.getMemberGroupParticipantStatus(group)) {
        case ApprovedStatus.UnApproved:
          message = 'Your request to join this group is pending';
          break;
        case ApprovedStatus.Rejected:
          message = 'Your request to join this group has been rejected';
          break;
        case ApprovedStatus.Pending:
          message = 'Your request to join this group is pending';
          break;
      }

      this._toast.info(this._translate.instant(message), { duration: 5000 });
    }
  }

  deleteGroup(group: GroupEntity, reRoute = false) {
    const dialogRef = isMobileView()
      ? this.bottomSheetService.open(MDeleteConfirmationDialogComponent, {
          ...DefaultDeleteDialogConfig,
          data: {
            type: ConfirmationDialogType.DELETE,
            text: group.name,
          },
        })
      : this._dialogService.open(ConfirmationDialogComponent, {
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset',
          data: {
            type: ConfirmationDialogType.DELETE,
            emphasize: group.name,
          },
        });

    dialogRef.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        if (result) {
          if (result === DeleteConfirmationOptions.CLOSE) {
            return;
          } else {
            this._useGroups
              .deleteGroup(group.id)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: () => {
                  this._toast.success(this._translate.instant('groups.delete success message'));
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.Group);
                  if (reRoute) {
                    this._router.navigate(['/groups']).then();
                    return;
                  }
                  this.isLoading = true;
                  this.fetchAllGroups();
                },
              });
          }
        }
      },
    });
  }

  leaveGroup(group: GroupEntity, reRoute = false) {
    const dialogRef = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      windowClass: 'dialog-overflow-unset',
      data: {
        type: ConfirmationDialogType.LEAVE,
        emphasize: group.name,
      },
    });

    dialogRef.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        if (result) {
          this._useGroups
            .leaveGroup(group.id)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this._toast.success(this._translate.instant('groups.leave success message'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Group);
                if (reRoute) {
                  this._reloadCurrentRoute(isMobileView() ? 'm/groups' : '/groups');
                } else {
                  this.fetchAllGroups();
                }
              },
            });
        }
      },
    });
  }

  joinGroup(group: GroupEntity) {
    const dialogRef = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      windowClass: 'dialog-overflow-unset',
      data: {
        type: ConfirmationDialogType.JOIN,
        emphasize: group.name,
      },
    });

    dialogRef.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        if (result) {
          this._useGroups
            .joinGroup(group.id)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                const message = group.isPrivate === GroupPrivate.True ? 'groups.waiting admin approval' : 'groups.join success message';
                this._toast.success(this._translate.instant(message));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Group);
              },
            });
        }
      },
    });
  }

  publishGroup(groupDetails: GroupEntity, approveUpdate = false) {
    const service$ = approveUpdate ? this._useGroups.approveGroupUpdate(groupDetails.id) : this._useGroups.approveGroup(groupDetails.id);

    const dialogRef = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      windowClass: 'dialog-overflow-unset',
      data: {
        type: ConfirmationDialogType.APPROVE,
        // cancelButtonText: this._translate.instant('Cancel'),
      },
    });

    dialogRef.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        if (result) {
          service$.pipe(untilDestroyed(this)).subscribe({
            next: () => {
              this._toast.success(this._translate.instant('Approve Successful'));
              this._socketService.emit(SocketEvents.SendNotification, NotificationType.Group);
              this._reloadCurrentRoute();
            },
          });
        }
      },
    });
  }

  denyGroup(groupDetails: GroupEntity) {
    this._dialogService
      .open(SingleInputCreateComponent, {
        closeButton: false,
        data: {
          title: this._translate.instant('Are you sure you want to unapprove this') + ' ' + this._translate.instant('Group'),
          label: this._translate.instant('Message'),
          inputType: 'textArea',
        },
      })
      .afterClosed$.pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            this._useGroups
              .denyGroup(groupDetails.id, res)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: () => {
                  this._toast.success(this._translate.instant('Deny Successful'));
                  this._socketService.emit(SocketEvents.SendNotification, NotificationType.Group);
                },
              });
          }
        },
      });
  }

  fetchAllGroups(): void {
    const filterQuery: FilterQuery = {
      ...this.pagination,
      ...this.filterQuery,
    };
    this._useGroups
      .getApprovedGroups(filterQuery)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (groupsList) => {
          this.pagination = groupsList.pagination;
          this.groups = groupsList.groups;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          log.error(error);
        },
      });
  }

  close() {
    if (!isMobileView()) {
      this._dialogService.closeAll();
    } else {
      this.bottomSheetService.close();
    }
  }

  save(btnRef?: ButtonComponent) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;
    formValue.teamId = GetTeamId();
    formValue.createdBy = GetUserId();
    formValue.groupAdmin = formValue.groupAdmin?.map((adminId: number) => {
      return {
        userId: adminId,
      } as GroupParticipant;
    });

    formValue.participants = formValue.participants.map((participant: any) => {
      return {
        userId: participant.id,
        approvedStatus: 1,
        keycloakId: participant.keycloakId,
      } as GroupParticipant;
    });

    // This part of code is omitted as Group Owner now refers to the creator of the group and assigned to the createdBy field

    // const index = formValue.participants.findIndex((participant) => participant.userId === formValue.groupCreator.id);
    // if (index === -1) {
    //   formValue.participants.push({
    //     userId: formValue.groupCreator.id,
    //     approvedStatus: 1,
    //   } as GroupParticipant);
    // }

    formValue.approvedStatus = this._permissionService.isAdmin() ? 1 : 0;
    if (!this.groupDetails) formValue.keycloakId = GetKeycloakId();

    delete formValue.currentUserId;

    const body = this.removeNullObjects(instanceToPlain(new GroupEntity(formValue)));
    body.isPrivate = formValue.isPrivate ? 1 : 0;

    body.group_creator = GetUserId(); // this is the user who is creating the group

    const formData = this.jsonToFromData(body, ['participants', 'groupAdmin']);

    if (this.selectedImage) {
      formData.append('file', this.selectedImage);
    }

    const service$ = this.groupDetails ? this._useGroups.updateGroup(this.groupDetails.id, formData) : this._useGroups.createGroup(formData);

    this.call(service$, btnRef)
      .then(() => {
        if (!this._permissionService.isAdmin()) {
          this._toast.success(this._translate.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
        } else {
          const message = this.groupDetails ? 'Update Successful' : 'Created Successfully';
          this._toast.success(this._translate.instant(message));
        }

        this._socketService.emit(SocketEvents.SendNotification, NotificationType.Group);

        if (!isMobileView()) {
          this._dialogService.closeAll();
        } else {
          this.bottomSheetService.closeAll();
        }

        this._reloadCurrentRoute();
      })
      .catch(() => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while creating or updating room');
      });
  }

  openEventDetails(event: GroupEventEntity) {
    const eventEntity = event as unknown as EventEntity;
    eventEntity.currentDate = event.dateFrom;

    if (!isMobileView()) {
      this._dialogService.open(EventDetailDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          id: event.id,
          event: eventEntity,
        },
      });
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _fetchData() {
    this._fetchGroupDetails();
    this._fetchGroupEvents();
  }

  protected _initForm() {
    this.form = this._formBuilder.group({
      name: [null, [Validators.required]],
      description: [null, [Validators.required]],
      participants: [null, [Validators.required]],
      image: [null],
      isPrivate: [false],
      groupAdmin: [null, [Validators.required]],
    });
  }

  protected _fetchGroupDetailsForForm() {
    this._useGroups
      .getGroupDetails(this.groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (groupDetails) => {
          this.groupDetails = groupDetails;
          this.loadings.groupDetails = false;
          this._fetchTeamUsers();
        },
      });
  }

  protected _patchForm() {
    if (!this.groupDetails) {
      return;
    }

    if (!this.groupDetails.participants) {
      return;
    }

    const participants = this.teamUsers.filter((teamUser) => {
      return this.groupDetails.participants.some((participant) => participant.userId === teamUser.id);
    });

    this.form.patchValue({
      name: this.groupDetails.name || '',
      description: stripHtml(this.groupDetails.description),
      participants: participants || [],
      isPrivate: this.groupDetails.isPrivate === GroupPrivate.True,
      image: this.groupDetails.imageUrl || '',
      groupCreator: this.groupDetails?.createdByUser && !this.groupDetails?.createdByUser?.deleted ? this.groupDetails.createdByUser : null,
      groupAdmin: this.groupDetails?.groupAdmins && this.groupDetails?.groupAdmins.length > 0 ? this.groupDetails.groupAdmins.map((admin) => admin.userId) : [],
    });
  }

  protected _fetchTeamUsers(patchForm = true) {
    this._useClub.getClubMembers().then((teamUsers) => {
      this.allTeamUsers = teamUsers;
      this.currentUser = teamUsers.find((user) => user.id === GetUserId());
      this.teamUsers = teamUsers;

      if (patchForm) {
        if (this.groupDetails) {
          this._patchForm();
        } else {
          this.form.get('groupCreator').setValue(this.currentUser);
        }
      }
    });
  }

  protected _fetchGroupMembers() {
    this._useGroups
      .getGroupMembers(this.groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (groupMemberList) => {
          this.groupMembers = groupMemberList.members;
          this.loadings.memberList = false;

          if (this.groupDetails?.groupCreator && this.groupDetails.groupCreator?.deleted) {
            this.groupDetails.groupCreator.firstName = 'Deleted';
            this.groupDetails.groupCreator.lastName = 'User';
            this.groupDetails.groupCreator.memberId = '0';
          }

          this.groupMembers.forEach((member) => {
            if (member?.user?.deleted) {
              member.user.firstName = 'Deleted';
              member.user.lastName = 'User';
              member.user.memberId = '0';
            }
          });

          if (this.updatedRecord && this.updatedRecord.groupCreator) {
            this._fetchTeamUsersAndUpdateAdminInUpdatedRecord();
          }

          this.filteredMembers = this.groupMembers.slice();
        },
      });
  }

  protected _fetchGroupEvents(pagination?: Pagination): void {
    pagination = pagination || {
      page: 1,
      pageSize: 5,
    };
    this._useGroups
      .getAllGroupEvents(this.groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (groupEventList) => {
          this.groupEvents = groupEventList;
          this.groupEvents.sort((a, b) => {
            const dateA = new Date(a.dateFrom).getTime();
            const dateB = new Date(b.dateFrom).getTime();
            return dateA - dateB;
          });

          this.loadings.eventList = false;
        },
      });
  }

  protected _fetchGroupDetails(): void {
    // Update tabs is just required in details page
    this.updateSelectionTabs = EnumToTabsSelectionArray(UpdateTabType, this._translate);

    this._useGroups
      .getGroupDetails(this.groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (groupDetails) => {
          if (!groupDetails) {
            this._router.navigate(['/groups']);
            return;
          }

          if (!this.isUserAdmin && groupDetails?.isPrivate && !groupDetails?.isApprovedMember) {
            this?._router.navigate([isMobileView() ? '/m/unauthorized' : '/unauthorized'], { queryParams: { returnUrl: '/groups' } });
            return;
          }

          this.groupDetails = groupDetails;

          this.originalRecord = this.groupDetails;

          if (this.originalRecord.updatedRecord) {
            this.updatedRecord = updateObjectProperties(this.originalRecord, this.originalRecord.updatedRecord);
          }

          if (this.updatedRecord) {
            this.currentUpdatedRecordTab = this._permissionService.isAdmin() ? UpdateTabType.UPDATE : UpdateTabType.CURRENT;
          }

          this.loadings.groupDetails = false;
          if (this.canUserSeeGroupTasks) {
            this._fetchGroupTasks();
          } else {
            this.loadings.taskList = false;
          }
          this._fetchGroupMembers();
        },
      });
  }

  private _fetchTeamUsersAndUpdateAdminInUpdatedRecord() {
    this._useClub.getClubMembers().then((teamUsers) => {
      if (this.updatedRecord && this.updatedRecord.groupCreator) {
        const creatorExistsInMembers = teamUsers.find((m) => m.id === +this.updatedRecord.groupCreator);
        if (creatorExistsInMembers) {
          this.groupDetails.groupCreator = creatorExistsInMembers;
        } else {
          this.groupDetails.groupCreator = null;
        }

        const adminExistsInMembers = teamUsers.find((m) => m.id === +this.updatedRecord.createdBy);
        if (adminExistsInMembers) {
          this.updatedRecord.createdByUser = adminExistsInMembers;
        } else {
          this.updatedRecord.createdByUser = null;
        }

        this.updatedRecord.participants.forEach((participant) => {
          const participantExistsInMembers = teamUsers.find((m) => m.id === +participant.userId);
          if (participantExistsInMembers) {
            participant.user = participantExistsInMembers;
          }
        });

        if (this.showUpdatedRecord) {
          this.filteredMembers = this.updatedRecord.participants.slice();
        }
      }
    });
  }

  private _fetchGroupTasks() {
    return this._useGroups
      .getGroupTasks(this.groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (tasks) => {
          this.groupTasks = tasks.groupTask;
          this.loadings.taskList = false;
        },
        error: (err) => {
          log.error('Error while fetching group tasks', err);
          this.loadings.taskList = false;
        },
      });
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }
}
